<template>
  <div class="content-template">
    <div :class="['container-fluid py-4 content-container', { 'with-sidebar': showContentNav }]">
      <div class="row justify-content-center">
        <div class="col-xl-10 col-lg-11 col-md-12 px-lg-4 px-md-3 px-sm-2">
          <!-- Main Heading -->
          <div class="page-header mb-4">
            <h1 class="display-4 fw-bold main-heading">{{ mainHeading }}</h1>
            <div class="sub-heading-container">
              <h2 class="sub-heading m-0 fw-semibold">{{ subHeading }}</h2>
            </div>
          </div>

          <!-- Document container -->
          <div class="document-container">
            <!-- Dynamic Sections and Subsections -->
            <section 
              v-for="(section, index) in sections" 
              :key="index" 
              :id="section.ref || ''" 
              class="content-section"
            >
              <div class="section-header">
                <h2 class="section-title fw-bold">{{ section.title }}</h2>
              </div>
              
              <div class="section-body">
                <div class="section-content" v-html="section.text"></div>

                <!-- Subsections -->
                <template v-if="section.subsections">
                  <section 
                    v-for="(sub, subIndex) in section.subsections" 
                    :key="subIndex" 
                    :id="sub.ref || ''" 
                    class="subsection mb-4"
                  >
                    <div class="subsection-header">
                      <h3 class="subsection-title fw-semibold">{{ sub.title }}</h3>
                    </div>
                    <div class="subsection-content" v-html="sub.text"></div>

                    <ul v-if="sub.listItems" class="list-styled">
                      <li v-for="(item, itemIndex) in sub.listItems" :key="itemIndex" v-html="item.text"></li>
                    </ul>
                    
                    <!-- Handle nested subsections if they exist -->
                    <div v-if="sub.subsections" class="nested-subsections">
                      <section
                        v-for="(nestedSub, nestedIndex) in sub.subsections"
                        :key="nestedIndex"
                        :id="nestedSub.ref || ''"
                        class="nested-subsection mt-3 mb-3"
                      >
                        <h4 class="nested-subsection-title fw-medium">{{ nestedSub.title }}</h4>
                        <div class="nested-subsection-content" v-html="nestedSub.text"></div>
                      </section>
                    </div>
                  </section>
                </template>
              </div>
            </section>

            <!-- Back to top button -->
            <div class="back-to-top-container">
              <button @click="scrollToTop" class="back-to-top-btn">
                <i class="bi bi-arrow-up"></i> Back to top
              </button>
            </div>
          </div>

          <!-- Slot for additional content -->
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ContentTemplate',
  props: {
    mainHeading: {
      type: String,
      required: true,
    },
    subHeading: {
      type: String,
      required: true,
    },
    sections: {
      type: Array,
      required: true,
    },
    showContentNav: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }
};
</script>

<style scoped>
.content-template {
  background-color: #ffffff;
  min-height: 100vh;
  padding-bottom: 3rem;
  width: 100%;
  overflow-x: hidden;
  margin-top: 80px; /* Add top margin to account for fixed header */
}

.content-container {
  width: 100%;
  max-width: 100%;
  transition: margin-left 0.3s ease;
}

.content-container.with-sidebar {
  margin-left: 0;
  padding-left: calc(270px + 1.5rem);
}

.page-header {
  position: relative;
  margin-top: 2rem;
}

.main-heading {
  color: #333;
  margin-bottom: 0.75rem;
  font-weight: 800;
}

.sub-heading-container {
  display: inline-block;
  background-color: #f2f5e7;
  border-left: 4px solid #87B140;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.sub-heading {
  color: #333;
  font-size: 1.5rem;
}

.document-container {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.07);
  overflow: hidden;
  border: 1px solid #eee;
  width: 100%;
}

.content-section {
  border-bottom: 1px solid #eee;
  padding-top: 20px;
  width: 100%;
}

.content-section:last-child {
  border-bottom: none;
}

.section-header {
  padding: 0.5rem 2rem 1rem 2rem;
  background-color: white;
}

.section-title {
  color: #87B140;
  font-size: 1.8rem;
  position: relative;
  display: inline-block;
  margin-bottom: 0;
  padding-bottom: 10px;
}

.section-title::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 3px;
  bottom: 0;
  left: 0;
  background-color: #87B140;
  border-radius: 3px;
}

.section-body {
  padding: 0 2rem 2rem 2rem;
  background-color: white;
}

.section-content {
  color: #555;
  line-height: 1.7;
  font-size: 1.05rem;
  margin-bottom: 1.5rem;
}

.subsection {
  background-color: white;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  border: 1px solid #eee;
  overflow: hidden;
}

.subsection-header {
  background-color: #f2f5e7;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #eee;
}

.subsection-title {
  color: #333;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0;
}

.subsection-content {
  color: #555;
  line-height: 1.7;
  font-size: 1.02rem;
  padding: 1.5rem;
}

.nested-subsections {
  padding: 0 1.5rem 1.5rem;
}

.nested-subsection {
  background-color: white;
  border-radius: 6px;
  padding: 1rem;
  border: 1px solid #eee;
}

.nested-subsection-title {
  color: #333;
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
}

.nested-subsection-content {
  color: #555;
  font-size: 1rem;
}

.subsection-content a,
.section-content a,
.nested-subsection-content a {
  color: #87B140;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.subsection-content a:hover,
.section-content a:hover,
.nested-subsection-content a:hover {
  color: #6a8e30;
  text-decoration: underline;
}

.list-styled {
  list-style-type: none;
  padding-left: 1.2rem;
  margin: 0 1.5rem 1.5rem;
  color: #555;
  line-height: 1.7;
}

.list-styled li {
  margin-bottom: 12px; /* Verbessert: Mehr Abstand zwischen Listenelementen */
  position: relative;
}

.list-styled li::before {
  content: '•';
  color: #87B140;
  font-weight: bold;
  position: absolute;
  left: -1.2rem;
  font-size: 1.2rem;
}

.back-to-top-container {
  display: flex;
  justify-content: center;
  padding: 1.5rem 0 0.5rem;
}

.back-to-top-btn {
  background-color: #87B140;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 10px 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 8px rgba(135, 177, 64, 0.3);
}

.back-to-top-btn:hover {
  background-color: #75a236;
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(135, 177, 64, 0.4);
}

/* Verbesserter Stil für Dokumentationsbilder */
.documentation-image {
  margin: 2.5rem auto;
  text-align: center;
  max-width: 90%;
}

.documentation-image img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  display: block;
  margin: 0 auto;
}

.documentation-image img:hover {
  transform: scale(1.02);
}

.documentation-image figcaption {
  margin-top: 1rem;
  font-style: italic;
  color: #666;
  font-size: 0.95rem;
}


/* Stil für den Logo-Container im Projektkontext */
.project-logo {
  text-align: center;
  margin: 2rem auto;
  max-width: 90%;
}

.project-logo img {
  max-width: 100%;
  height: auto;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}

/* Hervorhebung von wichtigen Texten */
.section-content strong, 
.subsection-content strong, 
.nested-subsection-content strong {
  color: #333;
  font-weight: 700;
}

/* Verbesserte Listen */
.list-styled li strong {
  display: inline-block;
}

/* Responsive Styles */
@media (max-width: 1199px) {
  .content-container.with-sidebar {
    padding-left: calc(270px + 1rem);
  }
  
  .section-header, .section-body {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
}

@media (max-width: 991px) {
  .content-container.with-sidebar {
    padding-left: 1.5rem;
    margin-left: 0;
  }
  
  .section-title {
    font-size: 1.6rem;
  }
  
  .subsection-header {
    padding: 0.8rem 1.2rem;
  }
  
  .subsection-content {
    padding: 1.2rem;
  }
}

@media (max-width: 768px) {
  .main-heading {
    font-size: 2.5rem;
  }
  
  .sub-heading {
    font-size: 1.3rem;
  }
  
  .section-header, .section-body {
    padding-left: 1rem;
    padding-right: 1rem;
  }
  
  .section-title {
    font-size: 1.4rem;
  }
  
  .subsection-title {
    font-size: 1.2rem;
  }
  
  .subsection-header {
    padding: 0.7rem 1rem;
  }
  
  .subsection-content {
    padding: 1rem;
  }
  
  .list-styled {
    margin: 0 1rem 1rem;
  }
  
  .documentation-image {
    margin: 1.5rem auto;
  }
  
  .documentation-image figcaption {
    font-size: 0.9rem;
  }
}

@media (max-width: 576px) {
  .main-heading {
    font-size: 2.2rem;
  }
  
  .section-header {
    padding: 0.5rem 1rem 0.8rem;
  }
  
  .section-body {
    padding: 0 1rem 1.5rem;
  }
  
  .back-to-top-btn {
    width: 100%;
    justify-content: center;
  }
  
  .documentation-image {
    margin: 1.5rem auto;
    max-width: 100%;
  }
}
</style>